import React, { useState } from "react";
import { useAgentSidebar } from "@/contexts/AgentSidebarContext";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Files } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";

interface ProposalSettingsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  bidId?: string;
  businessId?: string;
  clientName?: string;
  bidDataContext?: any;
}

export function ProposalSettingsSheet({
  isOpen,
  onClose,
  bidId,
  businessId,
  clientName,
  bidDataContext,
}: ProposalSettingsSheetProps) {
  const { enterResolutionMode } = useAgentSidebar();
  const { user } = useUser();
  const subscription = useQuery(api.subscriptions.getUserSubscription, user ? {} : "skip");
  const hasPurchasedCredits = Boolean((subscription as any)?.purchasedCredits && (subscription as any).purchasedCredits > 0);
  const hasActivePaidPlan = Boolean(subscription && subscription.status === "active" && (subscription.amount || 0) > 0);
  const isCreditBlocked = subscription !== undefined && !hasPurchasedCredits && !hasActivePaidPlan;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [tone, setTone] = useState("professional");
  const [pricingDisplay, setPricingDisplay] = useState("summary");
  const [customInstructions, setCustomInstructions] = useState("");

  // Included Sections State (aligned with actual proposal template)
  // Note: Scope of Work and Pricing are always required and not shown as toggles
  const [includedSections, setIncludedSections] = useState({
    coverLetter: true,
    executiveSummary: true,
    aboutUs: true, // Maps to "About Us" section in template
    whyChooseUs: true, // Contains focus areas
    nextSteps: true,
    thankYou: true,
    authorization: true,
  });

  // Focus Areas State
  const [focusAreas, setFocusAreas] = useState<string[]>([]);

  const toggleSection = (key: keyof typeof includedSections) => {
    setIncludedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFocusArea = (area: string) => {
    setFocusAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
  };

  const handleGenerate = () => {
    if (isCreditBlocked) {
      setShowUpgradeModal(true);
      return;
    }

    // Construct the prompt based on settings
    const sectionsList = Object.entries(includedSections)
      .filter(([_, included]) => included)
      .map(([key]) => key.replace(/([A-Z])/g, " $1").trim()) // CamelCase to Title Case
      .join(", ");

    const focusAreasText =
      focusAreas.length > 0 ? `Focus on these key areas: ${focusAreas.join(", ")}.` : "";

    const pricingInstruction =
      pricingDisplay === "itemized"
        ? "Include an itemized pricing breakdown."
        : pricingDisplay === "detailed"
          ? "Include a detailed pricing breakdown with labor and supplies."
          : "Provide a summary of the total monthly cost.";

    // Use Business Assistant to generate HTML proposal via resolution mode
    enterResolutionMode("generate-proposal", {
      bidId,
      businessId,
      clientName,
      templateStyle: "standard",
      tone,
      pricingInstruction,
      focusAreasText,
      customInstructions,
      sectionsList,
      bidDataContext,
    });

    onClose();
  };

  return (
    <>
      <UpgradePromptDialog
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to Starter"
        description="Access your AI Business Assistant to generate complete, professional proposals instantly."
        feature="AI Proposal Generation"
        planName="Starter"
        planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
      />
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="flex h-full w-[400px] flex-col sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Files className="h-5 w-5 text-blue-600" />
            Proposal Settings
          </SheetTitle>
          <SheetDescription>Configure how your proposal should be generated.</SheetDescription>
        </SheetHeader>

        <ScrollArea className="-mr-4 mt-6 flex-1 pr-4">
          <div className="space-y-6 px-2 py-6">
            {/* Tone */}
            <div className="space-y-2">
              <Label>Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="persuasive">Persuasive</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="formal">Formal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Included Sections */}
            <div className="space-y-3">
              <Label>Optional Sections</Label>
              <p className="text-xs text-muted-foreground">
                Cover Page, Scope of Work, and Pricing are always included.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="coverLetter"
                    checked={includedSections.coverLetter}
                    onCheckedChange={() => toggleSection("coverLetter")}
                  />
                  <Label htmlFor="coverLetter">Cover Letter</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="executiveSummary"
                    checked={includedSections.executiveSummary}
                    onCheckedChange={() => toggleSection("executiveSummary")}
                  />
                  <Label htmlFor="executiveSummary">Executive Summary</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="aboutUs"
                    checked={includedSections.aboutUs}
                    onCheckedChange={() => toggleSection("aboutUs")}
                  />
                  <Label htmlFor="aboutUs">About Us</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="whyChooseUs"
                    checked={includedSections.whyChooseUs}
                    onCheckedChange={() => toggleSection("whyChooseUs")}
                  />
                  <Label htmlFor="whyChooseUs">Why Choose Us</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nextSteps"
                    checked={includedSections.nextSteps}
                    onCheckedChange={() => toggleSection("nextSteps")}
                  />
                  <Label htmlFor="nextSteps">Next Steps</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="thankYou"
                    checked={includedSections.thankYou}
                    onCheckedChange={() => toggleSection("thankYou")}
                  />
                  <Label htmlFor="thankYou">Thank You Page</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="authorization"
                    checked={includedSections.authorization}
                    onCheckedChange={() => toggleSection("authorization")}
                  />
                  <Label htmlFor="authorization">Authorization</Label>
                </div>
              </div>
            </div>

            {/* Pricing Display */}
            <div className="space-y-3">
              <Label>Pricing Display</Label>
              <RadioGroup
                value={pricingDisplay}
                onValueChange={setPricingDisplay}
                className="flex flex-col space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="summary" id="summary" />
                  <Label htmlFor="summary">Summary (Total Monthly Price only)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="itemized" id="itemized" />
                  <Label htmlFor="itemized">Itemized (Breakdown by service type)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="detailed" id="detailed" />
                  <Label htmlFor="detailed">Detailed (Full breakdown with labor/supplies)</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Focus Areas */}
            {includedSections.whyChooseUs && (
              <div className="space-y-3">
                <Label>Focus Areas (Why Choose Us)</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Green Cleaning",
                    "Reliability",
                    "Cost Savings",
                    "Quality Assurance",
                    "Experience",
                    "Flexibility",
                    "Safety & Compliance",
                    "Technology",
                    "Trained Staff",
                    "Fast Response",
                    "Sustainability",
                    "Local Presence",
                    "Communication",
                    "Insurance & Bonding",
                    "Custom Solutions",
                  ].map((area) => (
                    <Badge
                      key={area}
                      variant={focusAreas.includes(area) ? "default" : "outline"}
                      className="cursor-pointer hover:bg-primary/90 hover:text-white"
                      onClick={() => toggleFocusArea(area)}
                    >
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Instructions */}
            <div className="space-y-2">
              <Label>Custom Instructions</Label>
              <Textarea
                placeholder="Any specific details to mention? (e.g., 'Emphasize our 24/7 support')"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                className="h-24"
              />
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="mt-auto border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Sparkles className="h-4 w-4" />
            Generate Proposal
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
    </>
  );
}
